import { useEffect, useRef, useState } from 'react';
import { Label, Slider } from '@design-system';
import { formatCurrency } from '@shared/formatCurrency';

import { snapPremiumToStep } from '@/features/guided-financial-planning/utils/premium-bounds-from-quotes';

const DEBOUNCE_MS = 200;

interface InlinePremiumSliderProps {
  value: number;
  min: number;
  max: number;
  step: number;
  onValueChange: (value: number) => void;
  disabled?: boolean;
}

export function InlinePremiumSlider({
  value,
  min,
  max,
  step,
  onValueChange,
  disabled = false,
}: InlinePremiumSliderProps) {
  // Local state for responsive dragging — parent only sees debounced values
  const [localValue, setLocalValue] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Sync local value when parent value changes (e.g. tab switch)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  function handleSliderChange(nextValues: number[]) {
    const raw = nextValues[0] ?? localValue;
    const snapped = snapPremiumToStep(raw, min, max, step);
    setLocalValue(snapped);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onValueChange(snapped);
    }, DEBOUNCE_MS);
  }

  const displayValue = snapPremiumToStep(localValue, min, max, step);

  return (
    <div className="p-2.25 bg-muted flex w-full items-center gap-3 rounded-lg border md:w-max">
      <Label className="text-foreground shrink-0 text-xs font-medium">Budget</Label>
      <div className="shrink-0 grow md:w-40">
        <Slider
          value={[displayValue]}
          onValueChange={handleSliderChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
        />
      </div>
      <span className="text-primary w-20 shrink-0 border-l text-right text-sm font-semibold tabular-nums">
        {formatCurrency(displayValue)}
      </span>
    </div>
  );
}
